import React, { useEffect, useRef, useState } from "react";

function publishDiscussionEvent(name, detail) {
  window.dispatchEvent(new CustomEvent(name, { detail }));
}

function normalizeDetail(detail = {}) {
  return {
    key: detail.key || `${detail.name || "file"}-${detail.sizeLabel || "size"}`,
    name: detail.name || "Unnamed file",
    extension: detail.extension || "FILE",
    sizeLabel: detail.sizeLabel || "",
    group: detail.group || "Attachment tray",
    state: detail.state || "queued",
    message: detail.message || ""
  };
}

function activityFor(type, file) {
  if (type === "safehub:dropzone-addedfile") {
    return { label: "File staged in tray", detail: `${file.name} ${file.sizeLabel}`.trim(), type: "upload" };
  }

  if (type === "safehub:dropzone-success") {
    return { label: "Direct upload ready", detail: `${file.name} is staged for attachment.`, type: "upload" };
  }

  if (type === "safehub:dropzone-error") {
    return { label: "Upload error", detail: file.message || `${file.name} could not be staged.`, type: "error" };
  }

  if (type === "safehub:dropzone-removedfile") {
    return { label: "Staged file removed", detail: file.name, type: "upload" };
  }

  return null;
}

export default function DiscussionUploadModal({
  buttonLabel = "Add attachments",
  heading = "Attach files",
  description = "",
  formHtml = "",
  initialOpen = false
}) {
  const [isOpen, setIsOpen] = useState(Boolean(initialOpen));
  const contentRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return undefined;

    const originalOverflow = document.body.style.overflow;
    const handleEscape = (event) => {
      if (event.key === "Escape") setIsOpen(false);
    };

    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", handleEscape);
    publishDiscussionEvent("discussion:activity", {
      label: "Attachment tray opened",
      detail: "Preparing new files without leaving the main dialogue.",
      type: "system",
      timestamp: new Date().toISOString()
    });

    return () => {
      document.body.style.overflow = originalOverflow;
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !contentRef.current) return undefined;

    const root = contentRef.current;

    const readFormState = () => {
      const focusField = root.querySelector('select[name$="[metadata][subcategory]"]');
      const promptField = root.querySelector('textarea[name$="[metadata][group_description]"]');

      return {
        focus: focusField?.value || "",
        prompt: promptField?.value || ""
      };
    };

    const emitState = (extra = {}) => {
      publishDiscussionEvent("discussion:upload-state", {
        ...readFormState(),
        ...extra
      });
    };

    const handleFieldChange = (event) => {
      const target = event.target;

      if (target.matches('select[name$="[metadata][subcategory]"]')) {
        emitState({ focus: target.value });
        if (target.value) {
          publishDiscussionEvent("discussion:activity", {
            label: "Upload focus changed",
            detail: target.value,
            type: "focus",
            timestamp: new Date().toISOString()
          });
        }
      }

      if (target.matches('textarea[name$="[metadata][group_description]"]')) {
        emitState({ prompt: target.value });
      }
    };

    const handleSubmit = () => {
      emitState();
      publishDiscussionEvent("discussion:activity", {
        label: "Upload batch submitted",
        detail: "New files are being linked to this discussion.",
        type: "upload",
        timestamp: new Date().toISOString()
      });
    };

    const dropzoneEventNames = [
      "safehub:dropzone-addedfile",
      "safehub:dropzone-processing",
      "safehub:dropzone-success",
      "safehub:dropzone-error",
      "safehub:dropzone-removedfile"
    ];

    const dropzoneHandlers = dropzoneEventNames.map((eventName) => {
      const handler = (event) => {
        const file = normalizeDetail(event.detail || {});
        publishDiscussionEvent("discussion:upload-file-state", {
          ...file,
          timestamp: new Date().toISOString()
        });

        const activity = activityFor(eventName, file);
        if (activity) {
          publishDiscussionEvent("discussion:activity", {
            ...activity,
            timestamp: new Date().toISOString()
          });
        }
      };

      root.addEventListener(eventName, handler);
      return [eventName, handler];
    });

    root.addEventListener("input", handleFieldChange);
    root.addEventListener("change", handleFieldChange);
    root.addEventListener("submit", handleSubmit);
    emitState({ modalOpen: true });

    return () => {
      root.removeEventListener("input", handleFieldChange);
      root.removeEventListener("change", handleFieldChange);
      root.removeEventListener("submit", handleSubmit);
      dropzoneHandlers.forEach(([eventName, handler]) => {
        root.removeEventListener(eventName, handler);
      });
    };
  }, [isOpen]);

  return (
    <>
      <button
        type="button"
        className="btn sub-discussion-btn sub-discussion-btn-secondary"
        onClick={() => setIsOpen(true)}
      >
        <i className="bi bi-paperclip me-2" />
        {buttonLabel}
      </button>

      {isOpen ? (
        <div
          className="sub-discussion-modal-backdrop"
          role="presentation"
          onClick={(event) => {
            if (event.target === event.currentTarget) setIsOpen(false);
          }}
        >
          <div className="sub-discussion-modal" role="dialog" aria-modal="true" aria-label={heading}>
            <div className="sub-discussion-modal-header">
              <div>
                <span className="sub-discussion-kicker">Attachment tray</span>
                <h2>{heading}</h2>
                <p>{description}</p>
              </div>
              <button
                type="button"
                className="sub-discussion-modal-close"
                aria-label="Close attachment tray"
                onClick={() => setIsOpen(false)}
              >
                <i className="bi bi-x-lg" />
              </button>
            </div>
            <div className="sub-discussion-modal-body" ref={contentRef} dangerouslySetInnerHTML={{ __html: formHtml }} />
          </div>
        </div>
      ) : null}
    </>
  );
}